/**
 * AI Translation Fallback Service
 *
 * Provides runtime translation fallback for missing keys using:
 * 1. Cached translations (localStorage)
 * 2. AI translation service (when available)
 * 3. Original English text (final fallback)
 *
 * This ensures the UI always displays meaningful text while
 * translations are being built up over time.
 *
 * Supported Languages:
 * - English (en) - Source language
 * - Spanish (es) - Español
 * - French (fr) - Français
 * - German (de) - Deutsch
 * - Portuguese (pt) - Português
 * - Chinese Simplified (zh) - 简体中文
 * - Chinese Traditional (zh-TW) - 繁體中文
 * - Japanese (ja) - 日本語
 * - Korean (ko) - 한국어
 * - Vietnamese (vi) - Tiếng Việt
 * - Arabic (ar) - العربية
 * - Russian (ru) - Русский
 */

import i18n from './config';

// Supported languages for AI translation
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', rtl: false },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', rtl: false },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', rtl: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', rtl: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', rtl: false },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', rtl: false },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', rtl: false },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', rtl: false },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', rtl: false },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

// Configuration
const CONFIG = {
  // Enable/disable AI translation (can be toggled via environment)
  enableAITranslation: import.meta.env.VITE_ENABLE_AI_TRANSLATION === 'true',

  // API endpoint for translation service
  translationEndpoint: import.meta.env.VITE_TRANSLATION_API_URL ?? '/api/translate',

  // Cache key prefix for localStorage
  cachePrefix: 'i18n_ai_cache_',

  // Cache duration in milliseconds (7 days for AI translations)
  cacheDuration: 7 * 24 * 60 * 60 * 1000,

  // Batch translation requests (collect for X ms before sending)
  batchDelay: 150,

  // Maximum batch size
  maxBatchSize: 100,

  // Retry failed translations after this delay
  retryDelay: 5000,

  // Maximum retries for failed translations
  maxRetries: 2,
};

// Types
interface CachedTranslation {
  text: string;
  timestamp: number;
  source: 'ai' | 'manual' | 'fallback';
  confidence?: number;
}

interface TranslationRequest {
  key: string;
  text: string;
  targetLang: string;
  resolve: (translation: string) => void;
  retries?: number;
}

// In-memory cache for current session
const memoryCache = new Map<string, string>();

// Pending translation requests for batching
let pendingRequests: TranslationRequest[] = [];
let batchTimeout: ReturnType<typeof setTimeout> | null = null;

// Track failed translations for retry
const failedTranslations = new Map<string, number>();

/**
 * Check if a language is supported
 */
export function isLanguageSupported(langCode: string): boolean {
  return SUPPORTED_LANGUAGES.some(lang => lang.code === langCode);
}

/**
 * Get language info by code
 */
export function getLanguageInfo(langCode: string) {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === langCode);
}

/**
 * Get cached translation from localStorage
 */
function getCachedTranslation(key: string, lang: string): string | null {
  const cacheKey = `${CONFIG.cachePrefix}${lang}_${key}`;

  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const data: CachedTranslation = JSON.parse(cached);

    // Check if cache is expired
    if (Date.now() - data.timestamp > CONFIG.cacheDuration) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data.text;
  } catch {
    return null;
  }
}

/**
 * Save translation to localStorage cache
 */
function setCachedTranslation(
  key: string,
  lang: string,
  text: string,
  source: 'ai' | 'manual' | 'fallback' = 'ai',
  confidence?: number
): void {
  const cacheKey = `${CONFIG.cachePrefix}${lang}_${key}`;

  const data: CachedTranslation = {
    text,
    timestamp: Date.now(),
    source,
    confidence,
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch {
    // localStorage might be full, clear old entries
    clearOldCache();
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch {
      // Still failed, just use memory cache
      console.warn('localStorage full, using memory cache only');
    }
  }
}

/**
 * Clear old cache entries
 */
function clearOldCache(): void {
  const keysToRemove: string[] = [];
  const now = Date.now();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CONFIG.cachePrefix)) {
      try {
        const data: CachedTranslation = JSON.parse(localStorage.getItem(key) || '{}');
        if (now - data.timestamp > CONFIG.cacheDuration) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key!);
      }
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Call AI translation API
 */
async function callTranslationAPI(
  texts: Array<{ key: string; text: string }>,
  targetLang: string
): Promise<Map<string, string>> {
  const results = new Map<string, string>();

  // Validate language is supported
  if (!isLanguageSupported(targetLang)) {
    console.warn(`Language ${targetLang} not supported for AI translation`);
    texts.forEach(({ key, text }) => results.set(key, text));
    return results;
  }

  if (!CONFIG.enableAITranslation) {
    // Return original texts if AI is disabled
    texts.forEach(({ key, text }) => results.set(key, text));
    return results;
  }

  try {
    const response = await fetch(CONFIG.translationEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        texts: texts.map(t => t.text),
        keys: texts.map(t => t.key),
        sourceLang: 'en',
        targetLang,
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation API error: ${response.status}`);
    }

    const data = await response.json();

    // Map results back to keys
    if (Array.isArray(data.translations)) {
      texts.forEach((item, index) => {
        const translation = data.translations[index] || item.text;
        results.set(item.key, translation);
        setCachedTranslation(item.key, targetLang, translation, 'ai', data.confidence?.[index]);
      });
    } else if (data.translation && texts.length === 1) {
      // Single translation response
      results.set(texts[0].key, data.translation);
      setCachedTranslation(texts[0].key, targetLang, data.translation, 'ai');
    }
  } catch (error) {
    console.warn('AI translation failed, using fallback:', error);
    // Return original texts on error
    texts.forEach(({ key, text }) => {
      results.set(key, text);
      // Don't cache fallbacks for too long - allow retry
      setCachedTranslation(key, targetLang, text, 'fallback');
    });
  }

  return results;
}

/**
 * Process batched translation requests
 */
async function processBatch(): Promise<void> {
  if (pendingRequests.length === 0) {
    batchTimeout = null;
    return;
  }

  const batch = pendingRequests.splice(0, CONFIG.maxBatchSize);
  const byLang = new Map<string, TranslationRequest[]>();

  // Group by target language
  batch.forEach(req => {
    const existing = byLang.get(req.targetLang) || [];
    existing.push(req);
    byLang.set(req.targetLang, existing);
  });

  // Process each language group
  for (const [lang, requests] of byLang) {
    const texts = requests.map(r => ({ key: r.key, text: r.text }));
    const translations = await callTranslationAPI(texts, lang);

    requests.forEach(req => {
      const translation = translations.get(req.key) || req.text;
      memoryCache.set(`${lang}_${req.key}`, translation);
      req.resolve(translation);
    });
  }

  // Process remaining if any
  if (pendingRequests.length > 0) {
    batchTimeout = setTimeout(processBatch, CONFIG.batchDelay);
  } else {
    batchTimeout = null;
  }
}

/**
 * Queue a translation request
 */
function queueTranslation(key: string, text: string, targetLang: string): Promise<string> {
  return new Promise(resolve => {
    pendingRequests.push({ key, text, targetLang, resolve });

    if (!batchTimeout) {
      batchTimeout = setTimeout(processBatch, CONFIG.batchDelay);
    }
  });
}

/**
 * Get translation with fallback
 * This is the main entry point for the fallback system
 */
export async function getTranslationWithFallback(
  key: string,
  defaultValue: string,
  targetLang?: string
): Promise<string> {
  const lang = targetLang || i18n?.language || 'en';

  // If English, just return the default value
  if (lang === 'en') {
    return defaultValue;
  }

  // Check memory cache first
  const memKey = `${lang}_${key}`;
  if (memoryCache.has(memKey)) {
    return memoryCache.get(memKey)!;
  }

  // Check localStorage cache
  const cached = getCachedTranslation(key, lang);
  if (cached) {
    memoryCache.set(memKey, cached);
    return cached;
  }

  // Check if i18next has this translation
  const i18nextTranslation = i18n.t(key, { defaultValue: '__NOT_FOUND__', lng: lang });
  if (i18nextTranslation !== '__NOT_FOUND__' && i18nextTranslation !== key) {
    memoryCache.set(memKey, i18nextTranslation);
    return i18nextTranslation;
  }

  // Queue for AI translation
  return queueTranslation(key, defaultValue, lang);
}

/**
 * Synchronous translation with fallback (for immediate rendering)
 * Returns cached translation or default value immediately
 */
export function getTranslationSync(
  key: string,
  defaultValue: string,
  targetLang?: string
): string {
  const lang = targetLang || i18n?.language || 'en';

  // If English, return default
  if (lang === 'en') {
    return defaultValue;
  }

  // Check memory cache
  const memKey = `${lang}_${key}`;
  if (memoryCache.has(memKey)) {
    return memoryCache.get(memKey)!;
  }

  // Check localStorage cache
  const cached = getCachedTranslation(key, lang);
  if (cached) {
    memoryCache.set(memKey, cached);
    return cached;
  }

  // Check i18next
  const i18nextTranslation = i18n.t(key, { defaultValue: '__NOT_FOUND__', lng: lang });
  if (i18nextTranslation !== '__NOT_FOUND__' && i18nextTranslation !== key) {
    memoryCache.set(memKey, i18nextTranslation);
    return i18nextTranslation;
  }

  // Queue async translation but return default for now
  queueTranslation(key, defaultValue, lang);

  return defaultValue;
}

/**
 * Preload translations for a set of keys
 * Useful for preloading translations when a component mounts
 */
export async function preloadTranslations(
  keys: Array<{ key: string; defaultValue: string }>,
  targetLang?: string
): Promise<void> {
  const lang = targetLang || i18n?.language || 'en';

  if (lang === 'en') return;

  const keysToLoad = keys.filter(({ key }) => {
    const memKey = `${lang}_${key}`;
    return !memoryCache.has(memKey) && !getCachedTranslation(key, lang);
  });

  if (keysToLoad.length === 0) return;

  const translations = await callTranslationAPI(
    keysToLoad.map(k => ({ key: k.key, text: k.defaultValue })),
    lang
  );

  translations.forEach((text, key) => {
    memoryCache.set(`${lang}_${key}`, text);
  });
}

/**
 * Export missing translations for manual review
 * Returns all keys that had to use fallback
 */
export function exportMissingTranslations(): Record<string, { text: string; source: string }> {
  const missing: Record<string, { text: string; source: string }> = {};

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CONFIG.cachePrefix)) {
      try {
        const data: CachedTranslation = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.source === 'fallback') {
          // Extract lang and translation key from cache key
          const parts = key.replace(CONFIG.cachePrefix, '').split('_');
          const lang = parts[0];
          const translationKey = parts.slice(1).join('_');
          missing[`${lang}:${translationKey}`] = {
            text: data.text,
            source: data.source,
          };
        }
      } catch {
        // Skip invalid entries
      }
    }
  }

  return missing;
}

/**
 * Get translation statistics
 */
export function getTranslationStats(): {
  totalCached: number;
  byLanguage: Record<string, { ai: number; fallback: number; manual: number }>;
  memoryCacheSize: number;
} {
  const stats = {
    totalCached: 0,
    byLanguage: {} as Record<string, { ai: number; fallback: number; manual: number }>,
    memoryCacheSize: memoryCache.size,
  };

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CONFIG.cachePrefix)) {
      try {
        const data: CachedTranslation = JSON.parse(localStorage.getItem(key) || '{}');
        const lang = key.replace(CONFIG.cachePrefix, '').split('_')[0];

        if (!stats.byLanguage[lang]) {
          stats.byLanguage[lang] = { ai: 0, fallback: 0, manual: 0 };
        }

        stats.byLanguage[lang][data.source]++;
        stats.totalCached++;
      } catch {
        // Skip invalid entries
      }
    }
  }

  return stats;
}

/**
 * Clear all translation cache
 */
export function clearTranslationCache(): void {
  memoryCache.clear();
  failedTranslations.clear();

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CONFIG.cachePrefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Clear cache for a specific language
 */
export function clearLanguageCache(langCode: string): void {
  const prefix = `${CONFIG.cachePrefix}${langCode}_`;

  // Clear memory cache
  for (const key of memoryCache.keys()) {
    if (key.startsWith(`${langCode}_`)) {
      memoryCache.delete(key);
    }
  }

  // Clear localStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

// Initialize: clean old cache on load
if (typeof window !== 'undefined') {
  clearOldCache();
}

export default {
  getTranslationWithFallback,
  getTranslationSync,
  preloadTranslations,
  exportMissingTranslations,
  clearTranslationCache,
  clearLanguageCache,
  getTranslationStats,
  isLanguageSupported,
  getLanguageInfo,
  SUPPORTED_LANGUAGES,
};
